import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Tag } from "lucide-react";
import { Link } from "react-router-dom";

import { MarketingFooter } from "@/components/layout/MarketingFooter";

import { recruitBlogPosts } from "./data";

export const BlogPage = () => {
  const featuredPost = recruitBlogPosts[0];
  const supportingPosts = recruitBlogPosts.slice(1);

  return (
    <div className="technical-home relative min-h-screen antialiased overflow-x-hidden">
      <section className="relative pt-28 pb-20">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-10 lg:grid-cols-12 lg:items-end"
          >
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em]">
                <BookOpen className="h-3.5 w-3.5" />
                theRecruit Journal
              </span>
              <h1 className="mt-8 font-display text-5xl leading-[0.95] tracking-tight md:text-7xl">
                Recruiting ideas with more signal, less noise.
              </h1>
            </div>
            <p className="max-w-xl text-lg leading-relaxed lg:col-span-4">
              Notes for recruiters and hiring leaders building evidence-backed hiring systems in an
              AI-first market.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid gap-px border bg-[var(--tr-tech-line)] lg:grid-cols-12">
            <motion.article
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.48, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[var(--tr-tech-panel)] p-7 md:p-10 lg:col-span-8"
            >
              <span className="inline-flex border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em]">
                Featured story
              </span>
              <h2 className="mt-6 font-display text-4xl leading-[1.02] tracking-tight md:text-5xl">
                {featuredPost.title}
              </h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed">{featuredPost.description}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-2 border px-3 py-1.5">
                  <Clock className="h-4 w-4" />
                  {featuredPost.readTime}
                </span>
                <span className="inline-flex items-center gap-2 border px-3 py-1.5">
                  <BookOpen className="h-4 w-4" />
                  {featuredPost.publishedOn}
                </span>
                {featuredPost.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 border px-3 py-1.5 text-xs font-semibold"
                  >
                    <Tag className="h-3.5 w-3.5" />
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/blog/${featuredPost.slug}`}
                className="mt-8 inline-flex items-center gap-2 border px-5 py-3 text-sm font-bold uppercase tracking-[0.14em]"
              >
                Read article
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.article>

            <motion.aside
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.48, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              className="technical-dark-section bg-[#1b1c19] p-7 md:p-10 lg:col-span-4"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.22em]">
                Why teams read this
              </p>
              <h3 className="mt-5 font-display text-3xl leading-tight">
                Operational thinking for modern recruiting teams.
              </h3>
              <p className="mt-5 leading-relaxed">
                We write about candidate signal, recruiting system design, and AI that supports
                better decisions instead of hiding them.
              </p>
              <div className="mt-7 grid gap-2">
                {[
                  "Evidence-backed hiring",
                  "Candidate ranking",
                  "Search systems",
                  "Recruiter ops",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="border border-[rgba(250,249,244,0.18)] px-3 py-2 text-sm"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-8 flex items-end justify-between gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em]">Archive</p>
              <h2 className="mt-3 font-display text-3xl leading-tight">All articles</h2>
            </div>
            <p className="hidden max-w-sm text-sm leading-relaxed text-[var(--tr-tech-muted)] md:block">
              Skimmable notes for people operating the hiring workflow, not generic AI commentary.
            </p>
          </div>
          <div className="grid gap-px border bg-[var(--tr-tech-line)] md:grid-cols-2 xl:grid-cols-3">
            {supportingPosts.map((post, index) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.34, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="flex min-h-[360px] flex-col bg-[var(--tr-tech-panel)] p-6"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="inline-flex items-center gap-1 border px-2.5 py-1">
                    <BookOpen className="h-4 w-4" />
                    {post.readTime}
                  </span>
                  <span>{post.publishedOn}</span>
                </div>
                <h3 className="mt-6 font-display text-3xl leading-[1.06] tracking-tight">
                  {post.title}
                </h3>
                <p className="mt-4 leading-relaxed">{post.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span key={tag} className="border px-2.5 py-1 text-xs font-semibold">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold uppercase tracking-[0.14em]"
                >
                  Open article
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default BlogPage;
